import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Search, Shield, Zap, Lock } from 'lucide-react';

const HomePage = () => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [serviceTermsUrl, setServiceTermsUrl] = useState('');
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Load service terms link from backend
  useEffect(() => {
    const loadServiceTerms = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/policy-links`);
        if (res.ok) {
          const data = await res.json();
          setServiceTermsUrl(data.service_terms_url || '');
        }
      } catch (err) {
        console.error('Error loading service terms:', err);
      }
    };
    loadServiceTerms();
  }, [backendUrl]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const domain = url.trim();
    
    if (!domain) {
      setError('Inserisci un dominio da analizzare');
      return;
    }

    // Clean the domain (remove http/https and trailing slashes)
    let cleanDomain = domain
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .toLowerCase();

    if (!cleanDomain.includes('.')) {
      setError('Inserisci un dominio valido (es. esempio.com)');
      return;
    }

    setError('');
    navigate(`/results/${encodeURIComponent(cleanDomain)}`);
  };

  return (
    <div className="min-h-screen bg-black">
      <Header showGuide={false} />
      
      <main className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-2xl text-center">
          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Smart <span className="text-red-500">Web Check</span>
          </h1>
          
          <p className="text-gray-400 mb-8 text-lg">
            Analizza la sicurezza e le criticità di qualsiasi sito web
          </p>

          {/* Search Form */}
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="relative">
              <div className="flex items-center bg-gray-900 border border-gray-700 rounded-xl overflow-hidden focus-within:border-red-500 transition-colors">
                <div className="flex items-center pl-5">
                  <Search className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setError('');
                  }}
                  placeholder="Inserisci un dominio (es. esempio.com)"
                  className="flex-1 px-4 py-4 bg-transparent text-white placeholder-gray-500 focus:outline-none text-lg"
                />
                <button
                  type="submit"
                  className="m-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all flex items-center gap-2"
                >
                  Analizza
                </button>
              </div>
              {error && (
                <p className="text-red-500 text-sm mt-2 text-left pl-2">{error}</p>
              )}
            </div>
          </form>

          {/* Features */}
          <div className="grid grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-500/10 rounded-xl mb-3">
                <Shield className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-white font-medium mb-1">Sicurezza</h3>
              <p className="text-gray-500 text-sm">SSL, Headers, Vulnerabilità</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-500/10 rounded-xl mb-3">
                <Zap className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-white font-medium mb-1">Performance</h3>
              <p className="text-gray-500 text-sm">DNS, Server, Porte</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-500/10 rounded-xl mb-3">
                <Lock className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-white font-medium mb-1">Privacy</h3>
              <p className="text-gray-500 text-sm">Cookies, Trackers, GDPR</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 py-4 text-center text-gray-600 text-sm">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span>© {new Date().getFullYear()} Smart Info Cyber Security</span>
          <span className="text-gray-700">•</span>
          <a 
            href={serviceTermsUrl || "https://www.smart-info.it/condizioni-servizio-webcheck"} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-red-500 transition-colors"
          >
            Condizioni di Servizio
          </a>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
